import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, FileWarning, RotateCcw, SearchX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import InputPanel from "@/components/InputPanel";
import ProcessingPanel from "@/components/ProcessingPanel";
import EmptyState from "@/components/EmptyState";
import ResultsPanel from "@/components/ResultsPanel";
import HistoryPanel from "@/components/HistoryPanel";
import BatchDetailDialog from "@/components/BatchDetailDialog";
import { emptyVendor, toPayload, toRow } from "@/lib/fields";
import { normalizeValue, summarize } from "@/lib/validation";
import { downloadCsv } from "@/lib/csv";
import {
  deleteBatch,
  errMessage,
  extractVendors,
  getBatch,
  getStats,
  listBatches,
  pushBatchToQueue,
  reextractBatch,
  saveBatchVendors,
} from "@/lib/api";
import { History } from "lucide-react";

export default function ManualExtractPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [emailBody, setEmailBody] = useState("");

  const [phase, setPhase] = useState("idle");
  const [rows, setRows] = useState([]);
  const [batch, setBatch] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reextracting, setReextracting] = useState(false);
  const [queuing, setQueuing] = useState(false);
  const [error, setError] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [announce, setAnnounce] = useState("");

  const stat = useMemo(() => summarize(rows), [rows]);

  const refreshHistory = useCallback(
    async (query) => {
      setHistoryLoading(true);
      try {
        const [list, s] = await Promise.all([listBatches(query ?? q), getStats()]);
        setHistory(list.items || []);
        setStats(s);
      } catch (e) {
        /* non critical */
      } finally {
        setHistoryLoading(false);
      }
    },
    [q]
  );

  useEffect(() => {
    refreshHistory("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => refreshHistory(q), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return undefined;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const runExtraction = useCallback(async () => {
    setPhase("processing");
    setError(null);
    try {
      const data = await extractVendors({ files, emailBody });
      setBatch(data);
      const newRows = (data.vendors || []).map(toRow);
      setRows(newRows);
      setDirty(false);
      setPhase("results");
      const s = summarize(newRows);
      setAnnounce(`Extraction complete. ${s.total} vendors found, ${s.invalid} invalid.`);
      if (newRows.length === 0) {
        toast.warning("No vendor details found", {
          description: "Try a clearer scan, or paste the email body along with the file.",
        });
      } else {
        toast.success(`${newRows.length} vendor${newRows.length === 1 ? "" : "s"} extracted`, {
          description: s.invalid
            ? `${s.invalid} row${s.invalid === 1 ? "" : "s"} need a PAN/IFSC fix before export.`
            : "Saved to history. Review, then send to the queue or export.",
        });
      }
      refreshHistory();
    } catch (e) {
      const msg = errMessage(e, "Extraction failed");
      setError(msg);
      setPhase("error");
      toast.error("Extraction failed", { description: msg });
      refreshHistory();
    }
  }, [files, emailBody, refreshHistory]);

  const handleCellChange = useCallback((rid, key, value) => {
    setRows((prev) =>
      prev.map((r) => (r._rid === rid ? { ...r, [key]: normalizeValue(key, value) } : r))
    );
    setDirty(true);
  }, []);

  const handleDeleteRow = useCallback((rid) => {
    setRows((prev) => prev.filter((r) => r._rid !== rid));
    setDirty(true);
    toast("Row removed", { description: "Save changes to keep this in history." });
  }, []);

  const handleAddRow = useCallback(() => {
    setRows((prev) => [...prev, emptyVendor()]);
    setDirty(true);
  }, []);

  const handleExport = useCallback(() => {
    if (!rows.length) {
      toast.error("Nothing to export");
      return;
    }
    const name = `vendors_${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(rows, name);
    toast.success("CSV exported", { description: `${rows.length} rows · 16 columns · ${name}` });
  }, [rows]);

  const handleSave = useCallback(async () => {
    if (!batch?.id) return;
    setSaving(true);
    try {
      const updated = await saveBatchVendors(batch.id, toPayload(rows));
      setBatch(updated);
      setRows((updated.vendors || []).map(toRow));
      setDirty(false);
      toast.success("Changes saved to history");
      refreshHistory();
    } catch (e) {
      toast.error("Could not save", { description: errMessage(e) });
    } finally {
      setSaving(false);
    }
  }, [batch, rows, refreshHistory]);

  const handleSendToQueue = useCallback(async () => {
    if (!batch?.id) return;
    setQueuing(true);
    try {
      if (dirty) await saveBatchVendors(batch.id, toPayload(rows));
      const res = await pushBatchToQueue(batch.id);
      setDirty(false);
      toast.success(`${res.created} row${res.created === 1 ? "" : "s"} sent to the queue`, {
        description: "Open the Queue screen to review, then export in Mysa format.",
        action: { label: "Open Queue", onClick: () => navigate("/queue") },
      });
      refreshHistory();
    } catch (e) {
      toast.error("Could not send to queue", { description: errMessage(e) });
    } finally {
      setQueuing(false);
    }
  }, [batch, dirty, rows, navigate, refreshHistory]);

  const handleReextract = useCallback(async () => {
    if (!batch?.id) return;
    setReextracting(true);
    try {
      const updated = await reextractBatch(batch.id);
      setBatch(updated);
      setRows((updated.vendors || []).map(toRow));
      setDirty(false);
      toast.success("Re-extracted from the stored email and files");
      refreshHistory();
    } catch (e) {
      toast.error("Re-extraction failed", { description: errMessage(e) });
    } finally {
      setReextracting(false);
    }
  }, [batch, refreshHistory]);

  const handleReset = useCallback(() => {
    setFiles([]);
    setEmailBody("");
    setRows([]);
    setBatch(null);
    setDirty(false);
    setError(null);
    setPhase("idle");
    toast("Cleared", { description: "Saved batches are still in History." });
  }, []);

  const handleLoadBatch = useCallback(async (id) => {
    try {
      const data = await getBatch(id);
      setBatch(data);
      setRows((data.vendors || []).map(toRow));
      setEmailBody(data.email_body || "");
      setFiles([]);
      setDirty(false);
      setError(null);
      setPhase("results");
      setHistoryOpen(false);
      toast.success("Batch loaded into the grid", {
        description: `${(data.vendors || []).length} rows · ${data.source_summary}`,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Could not load batch", { description: errMessage(e) });
    }
  }, []);

  const handleDeleteBatch = useCallback(
    async (id) => {
      try {
        await deleteBatch(id);
        toast.success("Batch deleted");
        if (batch?.id === id) {
          setBatch(null);
          setRows([]);
          setPhase("idle");
          setDirty(false);
        }
        refreshHistory();
      } catch (e) {
        toast.error("Could not delete", { description: errMessage(e) });
      }
    },
    [batch, refreshHistory]
  );

  const historyProps = {
    items: history,
    loading: historyLoading,
    q,
    setQ,
    onRefresh: () => refreshHistory(),
    onLoad: handleLoadBatch,
    onOpenDetail: (id) => {
      setDetailId(id);
      setDetailOpen(true);
    },
    onDelete: handleDeleteBatch,
    activeId: batch?.id,
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight sm:text-xl">Manual extract</h1>
          <p className="text-xs text-muted-foreground sm:text-sm">
            Upload vendor PDFs/images or paste an email body — outside the Gmail inbox flow.
            {stats ? ` ${stats.total_batches} batches · ${stats.total_vendors} vendors saved.` : ""}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 bg-card lg:hidden"
          onClick={() => setHistoryOpen(true)}
          data-testid="history-open-button"
        >
          <History className="h-4 w-4" aria-hidden="true" /> History
          {history.length ? (
            <span className="ml-1 rounded-full bg-secondary px-1.5 text-[10px] font-semibold">
              {history.length}
            </span>
          ) : null}
        </Button>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_356px]">
        <div className="min-w-0 space-y-4">
          <InputPanel
            files={files}
            setFiles={setFiles}
            emailBody={emailBody}
            setEmailBody={setEmailBody}
            onExtract={runExtraction}
            busy={phase === "processing"}
            onReset={handleReset}
            onNotify={(m) => toast.error("Some files were skipped", { description: m })}
          />

          {phase === "processing" ? (
            <ProcessingPanel fileCount={files.length} hasEmail={!!emailBody.trim()} />
          ) : null}

          {phase === "error" ? (
            <section className="vde-card border-danger-border p-4 sm:p-5" data-testid="error-alert" role="alert">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-danger-surface">
                  <AlertTriangle className="h-4 w-4 text-danger-text" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-danger-text">Extraction failed</p>
                  <p className="mt-1 break-words text-xs text-muted-foreground">{error}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Your input was still saved to History. You can retry, or try fewer/clearer files.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" className="gap-1.5" onClick={runExtraction} data-testid="retry-extraction-button">
                      <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Retry extraction
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-card"
                      onClick={() => {
                        setPhase("idle");
                        setError(null);
                      }}
                      data-testid="dismiss-error-button"
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          {phase === "results" && rows.length > 0 ? (
            <ResultsPanel
              rows={rows}
              batch={batch}
              dirty={dirty}
              saving={saving}
              reextracting={reextracting}
              queuing={queuing}
              onCellChange={handleCellChange}
              onDeleteRow={handleDeleteRow}
              onAddRow={handleAddRow}
              onExport={handleExport}
              onSave={handleSave}
              onReextract={handleReextract}
              onSendToQueue={handleSendToQueue}
            />
          ) : null}

          {phase === "results" && rows.length === 0 ? (
            <section className="vde-card p-6 text-center" data-testid="no-vendors-state">
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-warning-surface">
                <SearchX className="h-5 w-5 text-warning-text" aria-hidden="true" />
              </div>
              <p className="text-sm font-semibold">No vendor details found in this input</p>
              <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                The document or email did not contain recognisable vendor information. Try a sharper
                scan, add the email body alongside the attachment, or add a row manually.
              </p>
              <div className="mt-4 flex justify-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 bg-card"
                  onClick={handleAddRow}
                  data-testid="add-row-empty-button"
                >
                  <FileWarning className="h-3.5 w-3.5" aria-hidden="true" /> Add a row manually
                </Button>
                {batch?.id ? (
                  <Button
                    size="sm"
                    className="gap-1.5"
                    onClick={handleReextract}
                    disabled={reextracting}
                    data-testid="reextract-empty-button"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Try again
                  </Button>
                ) : null}
              </div>
            </section>
          ) : null}

          {phase === "idle" ? <EmptyState /> : null}
        </div>

        <aside className="hidden lg:sticky lg:top-[92px] lg:block lg:max-h-[calc(100vh-112px)]">
          <HistoryPanel {...historyProps} />
        </aside>
      </div>

      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent
          side="right"
          className="w-full max-w-[420px] bg-background p-0 sm:max-w-[420px]"
          data-testid="history-drawer"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Extraction history</SheetTitle>
          </SheetHeader>
          <div className="h-full p-3 pt-10">
            <HistoryPanel {...historyProps} />
          </div>
        </SheetContent>
      </Sheet>

      <BatchDetailDialog
        batchId={detailId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onLoad={handleLoadBatch}
      />

      <div aria-live="polite" className="sr-only">
        {announce}
      </div>
    </div>
  );
}
